import React, {useRef, useState, useEffect} from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Button, TextField, Link, Box, Typography, Stack, FormHelperText } from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';

import api from '../../api/api';
import config from '../../config';
import { getHubspotUTK } from '../../utils/utils';
import { SSOButtons } from '../SSOButtons';
import { ArrowForwardIcon } from '../icons';

export const StepOne = ({nextStepCallback}) => {
  const recaptchaRef = useRef(null);
  const [recaptchaClicked, setRecaptchaClicked] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [trackingCode, setTrackingCode] = useState(null);

  const formik = useFormik({
    initialValues: {
      email: '',
      submit: ''
    },
    validationSchema: Yup.object({
      email: Yup
        .string()
        .email('Must be a valid email')
        .max(254)
        .required('Email is required')
        .test(
          'is-valid-domain',
          'Email must include a valid domain with a top-level domain (e.g., .com, .org)',
          value =>
            value
              ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
              : true // Allow empty string for other validations to handle required
        )
    }),
    onSubmit: async (values, helpers) => {
      const recaptchaToken = recaptchaRef.current.getValue(); 
      if (recaptchaToken === '') {
        helpers.setErrors({ submit: 'Please complete the reCAPTCHA' });
        return;
      }
      try {
        const response = await api.post('/verify-email', {
          email: values.email,
          recaptcha_value: recaptchaToken,
          tracking_code: trackingCode
        });
        if(response.status === 201) {
          nextStepCallback(2, values.email.toString());
        }
        else {
          recaptchaRef.current.reset();
          helpers.setErrors({ submit: 'An unexpected error occurred. Please try again!' });
        }
      } catch (error) {
        recaptchaRef.current.reset();
        if (error.response && error.response.data && error.response.data.message) {
          helpers.setErrors({ submit: error.response.data.message });
        }else {
          helpers.setErrors({ submit: 'An unexpected error occurred. Please try again!' });
        }
        return;
      }
    }

  })

  useEffect(() => {
    const error = searchParams.get('error');
    setTrackingCode(getHubspotUTK());
    if (error) {
      navigate(window.location.pathname, { replace: true });
      setRecaptchaClicked(false);
      let errorMsg = 'Signup failed. Please try again.';
      if (error === 'non_corporate_email') {
        errorMsg = 'Please use a corporate email address';
      } else if (error === 'domain_already_registered') {
        errorMsg = 'An organization with this email domain is already registered';
      } else if (error === 'admin_already_exists') {
        errorMsg = 'This email is already registered. Please sign in to continue.';
      }

      formik.setErrors({ sso: errorMsg });
    }
  }, [formik, searchParams, navigate]);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 400,
        mx: 'auto',
      }}
    >
      <Stack
        sx={{
          mb: 2
        }}
      >
        <Typography
          component="h2"
          variant="h3"
          sx={{
            mb: 1.5
          }}
        >
          Get started with Keystrike
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
        >
          Already have an account?
          {" "}
          <Link href="https://admin.keystrike.com">
            Sign in
          </Link>
        </Typography>
      <Stack spacing={2}
        sx={{
          mb: 2,
          mt: 4
        }}>
        <SSOButtons />
        {formik.errors.sso && (
          <FormHelperText
            data-testid="errorMSG"
            error
          >
            {formik.errors.sso}
          </FormHelperText>
        )}
      </Stack>
      <Stack
        spacing={2}
        direction={'row'}
        alignItems={'center'}
        sx={{
          my: 3
        }}
      >
        <Box
          sx={{
            flex: 1,
            borderBottom: 1,
            borderColor: 'neutral.200'
          }}
        />
        <Typography
          variant="overline"
          sx={{
            color: 'neutral.600'
          }}
        >
          OR
        </Typography>
        <Box
          sx={{
            flex: 1,
            borderBottom: 1,
            borderColor: 'neutral.200'
          }}
        />
      </Stack>
      </Stack>
        <Box
          component="form"
          noValidate
          onSubmit={formik.handleSubmit}
        >
          <Stack
            spacing={2}
            sx={{
              mb: 2
            }}
          >
            <TextField
              error={!!(formik.touched.email && formik.errors.email)}
              margin="normal"
              fullWidth
              helperText={formik.touched.email && formik.errors.email}
              id="email"
              label="Work email"
              placeholder="you@company.com"
              name="email"
              autoComplete="email"
              type="email"
              data-testid="emailInput"
              autoFocus
              onChange={formik.handleChange}
              value={formik.values.email}
              inputProps={{ maxLength: 254 }}
            />
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={config.recaptchaSiteKey}
              onChange={() => setRecaptchaClicked(true)}
            /> 
            {formik.errors.submit && (
              <FormHelperText
                data-testid="errorMSG"
                error
              >
                {formik.errors.submit}
              </FormHelperText>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={formik.isSubmitting || !formik.isValid || !formik.dirty || !recaptchaClicked}
              disableFocusRipple
              endIcon={<ArrowForwardIcon />}
            >
              Continue
            </Button>
          </Stack>
          <Typography variant="body2" sx={{ color: 'neutral.600' }}>
            By registering you accept our
            {" "}
            <Link
              href="https://keystrike.com/terms"
              target="_blank"
            >
              Terms of Service
            </Link>.
          </Typography>
        </Box>
    </Box>
  );
}